import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import FAQSchema from "@/components/seo/FAQSchema";

const FAQSection = () => {
  const faqData = [
    {
      question: "What is Shadow AI in AuraXP?",
      answer: "Shadow AI refers to the use of artificial intelligence tools or models by employees without the knowledge or approval of their organization's IT or cybersecurity teams. This includes public chatbots, unapproved coding assistants, or generative AI tools that can inadvertently expose sensitive data. AuraXP by Spharaka Networks provides Shadow AI detection and governance, identifying unauthorized AI usage across the enterprise. It helps prevent data leakage, compliance violations, and unverified AI outputs, offering full visibility and control over AI activity. With AuraXP, organizations can securely enable innovation while maintaining AI security and governance compliance."
    },
    {
      question: "What does Organizational Memory mean in AuraXP?",
      answer: "AuraXP's Organizational Memory capability enables enterprises to retain and reuse institutional intelligence over time. It continuously learns from security incidents, investigations, analyst insights, and behavioral data, creating a persistent layer of context-aware intelligence. Even when personnel change, AuraXP preserves this knowledge, ensuring that organizational wisdom, patterns, and decision logic remain intact. This transforms AuraXP into an AI-powered cybersecurity knowledge base, helping teams make faster, more accurate decisions while maintaining operational continuity and resilience."
    },
    {
      question: "What is the Neural Threat Graph in AuraXP?",
      answer: "The Neural Threat Graph is AuraXP's dynamic intelligence framework that maps relationships between users, systems, data, and behaviors across your digital ecosystem. Powered by neural network-based analytics, it continuously identifies hidden attack vectors, lateral movement, and anomalous activity in real time. This interconnected graph allows AuraXP to deliver proactive threat detection, faster root-cause analysis, and predictive defense capabilities, offering a 360-degree view of enterprise cybersecurity posture."
    },
    {
      question: "What does Trustworthy AI mean in AuraXP?",
      answer: "Trustworthy AI in AuraXP ensures that every AI-driven decision or recommendation is transparent, explainable, and compliant with global security and privacy standards. AuraXP's ethical AI framework eliminates bias, safeguards sensitive data, and maintains full auditability across all autonomous actions. Designed for regulated and high-security environments, AuraXP guarantees AI reliability, compliance readiness, and decision traceability, building confidence in AI-powered cybersecurity automation."
    },
    {
      question: "What is Behavioral Intelligence in AuraXP?",
      answer: "Behavioral Intelligence in AuraXP enables advanced threat detection by learning how users, systems, and entities behave under normal conditions. It continuously monitors activity, access patterns, and environmental signals to detect insider threats, compromised identities, or suspicious anomalies in real time. By applying behavioral analytics and machine learning, AuraXP minimizes false positives and provides context-rich threat intelligence, enhancing both accuracy and response speed."
    },
    {
      question: "What is Cognitive Threat Reasoning in AuraXP?",
      answer: "Cognitive Threat Reasoning is AuraXP's advanced AI-driven capability that mimics human analytical thinking to investigate, reason, and respond to complex cyber threats autonomously. Rather than only flagging anomalies, AuraXP analyzes intent, context, and impact across multiple data streams. It correlates threat signals, builds logical inferences, and autonomously executes or recommends responses, delivering machine-speed investigation with human-level reasoning. This empowers enterprises to maintain continuous, adaptive, and autonomous cybersecurity defense."
    }
  ];

  return (
    <section className="py-16 bg-background" id="faq">
      <FAQSchema faqs={faqData} title="AuraXP Frequently Asked Questions" />
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Frequently Asked <span className="text-[#8932c8]">Questions</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Everything you need to know about AuraXP
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqData.map((faq, index) => (
              <AccordionItem key={`item-${index + 1}`} value={`item-${index + 1}`} className="border rounded-lg border-border px-6">
                <AccordionTrigger className="text-xl font-semibold py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
